<script setup>
import { computed, inject } from 'vue';
import { pageHeaderHasContent, pageHeaderTitle, starterTitleStyle } from '../blockContent.js';
import { accentParts, hasLink, isExternal, styleVars } from '../Trailhead/shared.js';
import { trailTotalsLine } from '../trailsPage.js';

// The page header as the Hub draws it. 'tint' and 'dark' are both the
// org's band (the hub has one header, and an inner page's title belongs
// in it); 'plain' is the title on the page ground over a rule, for a
// page that should read quieter. Same config as ThPageHeader, including
// the trail_totals eyebrow.
const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const plain = computed(() => props.config.tone === 'plain');
const titleParts = computed(() => accentParts(pageHeaderTitle(props.config, editing)));
const cta = computed(() => props.config.cta || {});
const eyebrow = computed(() => (props.config.eyebrow_source === 'trail_totals'
    ? trailTotalsLine(props.site_data)
    : props.config.eyebrow));
// A header with nothing but the starter "Page title" draws nothing for
// a visitor; the editor keeps it (Mus, 2026-10-11).
const editing = inject('tiqEditorCanvas', false);
const hasContent = computed(() => editing || pageHeaderHasContent(props.config, eyebrow.value));
</script>

<template>
    <section v-if="hasContent" :id="config.anchor_id || null" class="th-block hub-pagehead" :class="{ 'hub-band': !plain }" :style="styleVars(config)">
        <div class="th-wrap hub-pagehead__inner" :class="{ 'hub-pagehead__inner--plain': plain }">
            <div class="hub-pagehead__text">
                <p v-if="eyebrow" :class="plain ? 'th-eyebrow' : 'hub-eyebrow-on-band'">{{ eyebrow }}</p>
                <h1 v-if="titleParts.length || editing" class="hub-pagehead__title" :style="starterTitleStyle(config, editing)">
                    <template v-for="(part, i) in titleParts" :key="i">
                        <span v-if="part.accent" :class="plain ? 'th-accent-word' : 'hub-pagehead__accent'">{{ part.text }}</span>
                        <template v-else>{{ part.text }}</template>
                    </template>
                </h1>
                <p v-if="config.subtitle" class="hub-pagehead__sub" :class="{ 'hub-soft': !plain }">{{ config.subtitle }}</p>
            </div>
            <a v-if="hasLink(cta)"
               class="th-btn"
               :class="plain ? 'th-btn--primary' : 'th-btn--ghost-light'"
               :href="cta.href || '#'"
               :target="isExternal(cta.href) ? '_blank' : null"
               :rel="isExternal(cta.href) ? 'noopener noreferrer' : null">{{ cta.label }}</a>
        </div>
    </section>
</template>

<style scoped>
.hub-pagehead__inner {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px 32px;
    padding-top: 26px;
    padding-bottom: 30px;
}
.hub-pagehead__inner--plain { border-bottom: 1px solid rgba(16, 26, 21, .1); padding-top: 32px; padding-bottom: 22px; }
.hub-pagehead__text { flex: 1 1 360px; min-width: 0; }
.hub-pagehead__title {
    margin: 8px 0 0;
    font-size: clamp(30px, 4.6vw, 38px);
    font-weight: 800;
    letter-spacing: -0.04em;
    line-height: 1.05;
    color: var(--text-heading, inherit);
}
.hub-pagehead__accent { color: var(--hub-detail, #dd9c34); }
.hub-pagehead__sub { font-size: 14px; line-height: 1.5; margin: 10px 0 0; max-width: 56ch; color: var(--text-sub, var(--hub-body)); }
</style>
